import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Avatar,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  Play,
  ChevronRight,
  Clock,
  ArrowUpRight,
  GitBranch,
  Lock,
  Link2,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Task, TaskStatus } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';
import ReopenedChip from '@/shared/components/ReopenedChip';
import { TaskContextType, TaskTablePermissions } from './types';
import { CATEGORY_STYLES, getCategoryStyle, getDeadlineStyle, TIMER_ACTIVE_BG } from './constants';
import { getUserRaciChip, getTaskLinkCounts, isTaskOverdue } from './utils';
import { User } from './permissions';

export interface MobileTaskCardProps {
  task: Task;
  context: TaskContextType;
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
  isTimerActive: boolean;
  isPickedForToday: boolean;
  showProjectName: boolean;
  parentTask?: Task;
  user: User | null;
  permissions: TaskTablePermissions;
  enableSelection?: boolean;
  isSelected?: boolean;
  onTaskClick: (taskId: string) => void;
  onCheckboxChange: (task: Task) => void;
  onStartTimer: (taskId: string, taskTitle: string) => void;
  onSelectionChange?: (taskId: string, selected: boolean) => void;
}

/**
 * MobileTaskCard - Renders a single task as a card for mobile view
 */
const MobileTaskCard: React.FC<MobileTaskCardProps> = ({
  task,
  context,
  currentRaciRole,
  isTimerActive,
  isPickedForToday,
  showProjectName,
  parentTask,
  user,
  permissions,
  enableSelection = false,
  isSelected = false,
  onTaskClick,
  onCheckboxChange,
  onStartTimer,
  onSelectionChange,
}) => {
  const linkCounts = getTaskLinkCounts(task);
  const deadlineStyle = getDeadlineStyle(task.dueDate, task.status);
  const raciChipInfo = user ? getUserRaciChip(task, user, currentRaciRole) : null;
  const overdue = isTaskOverdue(task);

  return (
    <Card
      sx={{
        borderRadius: 2,
        bgcolor: isTimerActive ? TIMER_ACTIVE_BG : 'white',
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
      onClick={() => onTaskClick(task.id)}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Top Row: Checkbox + Title + Timer + Arrow */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          {enableSelection && (
            <Checkbox
              checked={isSelected}
              disabled={!permissions.canDelete}
              onChange={(e) => {
                e.stopPropagation();
                onSelectionChange?.(task.id, e.target.checked);
              }}
              sx={{ p: 0, mt: 0.25 }}
              inputProps={{ 'aria-label': `Select task ${task.title}` }}
            />
          )}
          <Checkbox
            checked={task.status === TaskStatus.COMPLETED}
            onChange={(e) => {
              e.stopPropagation();
              onCheckboxChange(task);
            }}
            disabled={!permissions.canToggleCheckbox}
            sx={{ p: 0, mt: 0.25 }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                textDecoration: task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4,
              }}
            >
              {task.title}
            </Typography>

            {/* Parent Task Link */}
            {parentTask && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskClick(parentTask.id);
                }}
              >
                <ArrowUpRight size={10} style={{ color: '#6366f1' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontSize: '0.65rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {parentTask.title}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Timer Button */}
          {task.status !== TaskStatus.COMPLETED && permissions.canUseTimer && (
            <Tooltip
              title={
                isTimerActive
                  ? 'Timer active'
                  : isPickedForToday
                    ? 'Start timer'
                    : "Pick in Today's Tasks first"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isTimerActive && isPickedForToday) {
                      onStartTimer(task.id, task.title);
                    }
                  }}
                  disabled={isTimerActive || !isPickedForToday}
                  sx={{ p: 0.5 }}
                >
                  <Play
                    size={16}
                    fill={isTimerActive ? '#16a34a' : '#4F46E5'}
                    style={{ color: isTimerActive ? '#16a34a' : '#4F46E5' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <ChevronRight size={16} style={{ color: '#94a3b8', marginTop: 2 }} />
        </Box>

        {/* Metadata Row: Priority + Status + Due Date */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            pl: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ '& .MuiChip-root': { height: 18, fontSize: '0.6rem' } }}>
            <PriorityBadge
              priority={task.priority}
              size="small"
            />
          </Box>

          <Box sx={{ '& .MuiChip-root': { height: 18, fontSize: '0.6rem' } }}>
            <StatusBadge
              status={task.status}
              size="small"
            />
          </Box>
          <ReopenedChip reopenedAt={task.reopenedAt} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Clock size={12} style={{ color: '#94a3b8' }} />
            <Typography variant="caption" sx={{ ...deadlineStyle, fontSize: '0.7rem' }}>
              {dayjs(task.dueDate).format('MMM D')}
            </Typography>
          </Box>

          {overdue && (
            <Chip
              label="Overdue"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: 'error.50',
                color: 'error.main',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {/* Indicators Row: Category + Subtasks + Dependencies */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            pl: 4,
            flexWrap: 'wrap',
          }}
        >
          {task.category && (() => {
            const style = getCategoryStyle(task.category) ?? CATEGORY_STYLES[task.category];
            if (!style) return null;
            return (
            <Chip
              label={style.label}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: style.bgcolor,
                color: style.color,
              }}
            />
            );
          })()}

          {linkCounts.subtasks > 0 && (
            <Tooltip title={`${linkCounts.subtasks} subtask${linkCounts.subtasks > 1 ? 's' : ''}`}>
              <Chip
                icon={<GitBranch size={10} />}
                label={linkCounts.subtasks}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: 'info.50',
                  color: 'info.main',
                  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {linkCounts.blockedBy > 0 && (
            <Tooltip title={`Blocked by ${linkCounts.blockedBy}`}>
              <Chip
                icon={<Lock size={10} />}
                label={linkCounts.blockedBy}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: 'warning.50',
                  color: 'warning.main',
                  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {linkCounts.blocking > 0 && (
            <Tooltip title={`Blocking ${linkCounts.blocking}`}>
              <Chip
                icon={<Link2 size={10} />}
                label={linkCounts.blocking}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: 'secondary.50',
                  color: 'secondary.main',
                  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Bottom Row: RACI + Assigned Users */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pl: 4,
          }}
        >
          {/* RACI Chip (My Tasks context) */}
          {context === 'myTasks' && raciChipInfo && (
            <Tooltip
              title={
                raciChipInfo.hasMultipleRoles
                  ? `Roles: ${raciChipInfo.allRoles.join(', ')}`
                  : raciChipInfo.label
              }
            >
              <Chip
                label={raciChipInfo.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: raciChipInfo.bgColor,
                  color: raciChipInfo.color,
                  border: raciChipInfo.hasMultipleRoles
                    ? `2px solid ${raciChipInfo.color}`
                    : 'none',
                }}
              />
            </Tooltip>
          )}

          {/* Project Name (if enabled) */}
          {showProjectName && (task.projectName || task.projectId) && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {task.projectName || task.projectId}
            </Typography>
          )}

          {/* Assigned Users */}
          <AvatarGroup max={3} sx={{ ml: 'auto' }}>
            {task.raci?.responsible?.map((userId, idx) => {
              const displayName = task.raciNames?.responsible?.[idx] || userId;
              return (
                <Tooltip key={userId} title={displayName}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.65rem',
                      bgcolor: 'primary.main',
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              );
            })}
          </AvatarGroup>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MobileTaskCard;
